/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import { memo, useEffect, useRef, useState } from "react";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { convertDate } from "@Services/luxonHelper";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { getTemplates } from "@Actions/Forms";
import CreateMail from "./createMail";
import MailSidebar from "./MailSidebar";
import { showToastNotification } from "@Reducers/toastSlice";
import NoMails from "./NoMails";
import ForwardModal from "./ForwardModal";
import { getProjectMailThreadsPageUrl } from "@Helpers";
import _ from "lodash";
import { renderUserTag, renderUserTagGroup } from "@CubeComponents/html/userTagDom";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { presetSectionsCodeObject } from "@Helpers/constants";
import "@Assets/css/colors.css";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { getSubColumns } from "@CubeComponents/html/SlickGrid1/customFields";
import Text from "@CubeComponents/html/Text/Text";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import type {
  DisableToolbarButtons,
  MailParams,
  MailsListProps,
  MailRecipient,
  FormTemplate,
} from "./mailbox.interfaces";
import {
  deleteThreadRequest,
  getMailsRequest,
  exportMailsToExcelRequest,
  mailsUrl,
} from "@Features/mailbox/api/mailbox.api";
import ExcelJS from "exceljs";
import { uploadStringToTemp } from "@Services/tempUpload";
import type { RootState } from "@Reducers/index";
import type { ProjectData } from "@Reducers/breadcrumbsdata";
import { GridPageLayout, GridPageHeaderLeft } from "@CubeComponents/html/GridPageLayout";
import type {
  SlickGrid1Ref,
  GridRowBase,
  GridClickEvent,
  SortCondition,
  GroupCondition,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/** A mail item rendered as a SlickGrid row. */
type MailRow = MailParams & GridRowBase;

/** A single section of a mail's form template (server `FormTemplate.template` element). */
type FormTemplateSection = FormTemplate["template"][number];

/** Column descriptor used by the mailbox grid (custom 5-arg formatter convention). */
interface MailGridColumn {
  id: string;
  field: string;
  name: string;
  minWidth?: number;
  filterType?: string;
  allowFiltering?: boolean;
  disableSorting?: boolean;
  excludeFromExport?: boolean;
  formatter?: (data: unknown, value: unknown, a: unknown, b: unknown, params: MailParams) => HTMLElement | string;
  _valueGetter?: (args: MailParams) => string;
  _excelValueGetter?: (args: MailParams) => string;
  [key: string]: unknown;
}

/** Action-menu item shape consumed by SelectDropDown's `items`/`select`. */
interface MailboxMenuItem {
  text: string;
  id: string;
  [key: string]: unknown;
}

interface MailboxHeaderProps {
  isExternalMailEnabled: boolean;
  projectData: ProjectData;
  headerThreeDotsItems: MailboxMenuItem[];
  handleSelectOption: (args: { item: { properties: { id?: string } } }) => void;
}

const MailboxHeader = memo(function MailboxHeader({
  isExternalMailEnabled,
  projectData,
  headerThreeDotsItems,
  handleSelectOption,
}: MailboxHeaderProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between pb-4">
      <GridPageHeaderLeft
        title={"Mailbox"}
        description={
          isExternalMailEnabled
            ? `${t("Project Mail Address")} : ${projectData.mailboxAddress}@${projectData.mailboxDomain}`
            : undefined
        }
      />
      {isExternalMailEnabled && (
        <div className="ms-auto">
          <SelectDropDown
            select={handleSelectOption}
            cssClass="e-caret-hide"
            className="p-0"
            items={headerThreeDotsItems}
            onClick={(e: React.SyntheticEvent) => e.stopPropagation()}
            data-testid="threeDotBtnMailbox"
          >
            <IconComponent name="threedots" className="cursor-pointer p-0" width="1.25rem" height="1.25rem" />
          </SelectDropDown>
        </div>
      )}
    </div>
  );
});

function MailsList(props: Readonly<MailsListProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dataGrid = useRef<SlickGrid1Ref<MailRow> | null>(null);
  const layout = useSelector((state: RootState) => state.layout);
  const projectData = useSelector((state: RootState) => state.projectData);
  const [columnDefinitions, setColumnDefinitions] = useState<MailGridColumn[]>([]);
  const [openMailSidebar, setOpenMailSidebar] = useState<boolean>(false);
  const [openNewMailModal, setOpenNewMailModal] = useState<boolean>(false);
  const [openForwardModal, setOpenForwardModal] = useState<boolean>(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState<boolean>(false);
  const [isExternalMailEnabled, setIsExternalMailEnabled] = useState<boolean>(false);
  const [formTemplates, setFormTemplates] = useState<FormTemplateSection[][]>([]);
  const [disableToolbarButtons, setDisableToolbarButtons] = useState<DisableToolbarButtons>({
    deleteMail: true,
    openSidebar: true,
  });
  const [selectedRowIds, setSelectedRowIds] = useState<ObjectIdString[]>([]);
  const [hasGridData, setHasGridData] = useState<boolean | null>(null);
  // `ProjectDataItem._id` is declared optional but is always present once the project page has
  // loaded; the id is typed once here, at the ref's declaration, rather than at each request.
  const gridQuery = useRef({ projectId: projectData._id as ObjectIdString });

  useEffect(() => {
    setIsExternalMailEnabled(Boolean(_.get(layout, "MAILBOX.ENABLE_EXTERNAL_MAILBOX", false)));
  }, [layout]);

  const mailNumberFormatter = (_data: unknown, _value: unknown, _a: unknown, _b: unknown, args: MailParams): string => {
    if (args.isExternal) {
      return `<div style="display:flex;align-items:center;gap:0.375rem;">
      <div>${args.mailNumber}</div>
      <div style="
        border-radius: 0.75rem;
        font-size: 0.75rem;
        padding: 0.1rem 0.5rem;
        background-color: #ffcf55;
        color: white;
      ">External</div>
    </div>`;
    }

    return `<div style="display:flex;align-items:center;gap:0.375rem;">
    <div>${args.mailNumber}</div>
  </div>`;
  };

  const sentByFormatter = (
    _data: unknown,
    _value: unknown,
    _a: unknown,
    _b: unknown,
    params: MailParams,
  ): HTMLElement => {
    const container = document.createElement("div");
    renderUserTag(container, { id: params.sentBy?._id, fullName: params.sentBy?.email });
    return container;
  };

  const sentToFormatter = (
    _data: unknown,
    _value: unknown,
    _a: unknown,
    _b: unknown,
    params: MailParams,
  ): HTMLElement => {
    const filteredUsers = params.sentTo?.filter((user: MailRecipient) => user._id || user.isExternal) || [];
    const userIdsForGroup = filteredUsers
      .map((item: MailRecipient) => item._id)
      .filter((id): id is NonNullable<MailRecipient["_id"]> => !!id);
    const container = document.createElement("div");
    container.className = "cursor-pointer flex";
    renderUserTagGroup(container, { ids: userIdsForGroup, maxAvatars: 5, size: "LSM" });
    return container;
  };

  const sentCCFormatter = (
    _data: unknown,
    _value: unknown,
    _a: unknown,
    _b: unknown,
    params: MailParams,
  ): HTMLElement => {
    const filteredUsers = params.sentCC?.filter((user: MailRecipient) => user._id || user.isExternal) || [];
    const userIdsForGroup = filteredUsers
      .map((item: MailRecipient) => item._id)
      .filter((id): id is NonNullable<MailRecipient["_id"]> => !!id);
    const container = document.createElement("div");
    container.className = "cursor-pointer flex";
    renderUserTagGroup(container, { ids: userIdsForGroup, maxAvatars: 5, size: "LSM" });
    return container;
  };

  const createdDateFormatter = (
    _data: unknown,
    _value: unknown,
    _a: unknown,
    _b: unknown,
    params: MailParams,
  ): HTMLElement => {
    const dateFormat = convertDate(Number(params.createdDate), "dateForTooltip");
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <ToolTipComponent position="bottom" content={dateFormat}>
        {convertDate(Number(params.createdDate), "dateForText")}
      </ToolTipComponent>,
    );
    return container;
  };

  const dueDateFormatter = (
    _data: unknown,
    _value: unknown,
    _a: unknown,
    _b: unknown,
    params: MailParams,
  ): HTMLElement => {
    const dateFormat = convertDate(Number(params.dueDate), "dateForTooltip");
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <ToolTipComponent position="bottom" content={dateFormat}>
        {convertDate(Number(params.dueDate), "dateForText")}
      </ToolTipComponent>,
    );
    return container;
  };

  const getTagColor = (tag: string): string => {
    if (!tag) return `var(--system-colour-1)`;
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = tag.codePointAt(i)! + ((hash << 5) - hash);
    }
    const colorIndex = (Math.abs(hash) % 46) + 1;
    return `var(--system-colour-${colorIndex})`;
  };

  const tagsFormatter = (
    _data: unknown,
    _value: unknown,
    _a: unknown,
    _b: unknown,
    params: MailParams,
  ): HTMLElement | string => {
    if (params.tags?.length) {
      const allTagsText = params.tags.join(", ");
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <ToolTipComponent position="bottom" content={untranslated(allTagsText)}>
          <div className="flex flex-nowrap overflow-hidden items-center">
            {params.tags.map((tag: string) => (
              <span
                key={tag}
                style={{
                  color: "white",
                  padding: "0.375rem 0.5rem",
                  marginInlineEnd: "0.25rem",
                  borderRadius: "1.25rem",
                  fontSize: "0.75rem",
                  lineHeight: "1",
                  backgroundColor: getTagColor(tag),
                  display: "inline-block",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </ToolTipComponent>,
      );
      return container;
    }
    return "-";
  };

  const responseTypeLabels: Record<string, string> = {
    ACKNOWLEDGE_BY: "Acknowledge By",
    REPLY_BY: "Reply By",
  };

  const responseTypeFormatter = (
    _data: unknown,
    _value: unknown,
    _a: unknown,
    _b: unknown,
    params: MailParams,
  ): string => responseTypeLabels[params.responseType || ""] || "-";

  const baseColumns: MailGridColumn[] = [
    {
      id: "mailNumber",
      field: "mailNumber",
      name: t("ID"),
      minWidth: 120,
      formatter: mailNumberFormatter,
      filterType: "Text",
    },
    {
      id: "subject",
      field: "subject",
      name: t("Subject"),
      minWidth: 120,
      filterType: "Text",
    },
    {
      id: "sentBy.name",
      field: "sentBy.name",
      name: t("From"),
      minWidth: 150,
      formatter: sentByFormatter,
      filterType: "Text",
      _valueGetter: (args: MailParams) => _.get(args, "sentBy.name") || _.get(args, "sentBy.email") || "",
      _excelValueGetter: (args: MailParams) => _.get(args, "sentBy.name") || _.get(args, "sentBy.email") || "",
    },
    {
      id: "sentTo",
      field: "sentTo",
      name: t("To"),
      minWidth: 150,
      formatter: sentToFormatter,
      allowFiltering: false,
      disableSorting: true,
      _valueGetter: (args: MailParams) =>
        (args.sentTo ?? [])
          .map((u: MailRecipient) => u.name || u.email || "")
          .filter(Boolean)
          .join(", "),
      _excelValueGetter: (args: MailParams) =>
        (args.sentTo ?? [])
          .map((u: MailRecipient) => u.name || u.email || "")
          .filter(Boolean)
          .join(", "),
    },
    {
      id: "sentCC",
      field: "sentCC",
      name: t("CC"),
      minWidth: 150,
      formatter: sentCCFormatter,
      allowFiltering: false,
      disableSorting: true,
      _valueGetter: (args: MailParams) =>
        (args.sentCC ?? [])
          .map((u: MailRecipient) => u.name || u.email || "")
          .filter(Boolean)
          .join(", "),
      _excelValueGetter: (args: MailParams) =>
        (args.sentCC ?? [])
          .map((u: MailRecipient) => u.name || u.email || "")
          .filter(Boolean)
          .join(", "),
    },
    {
      id: "createdDate",
      field: "createdDate",
      name: t("Date"),
      minWidth: 150,
      formatter: createdDateFormatter,
      filterType: "Date",
      _valueGetter: (args: MailParams) =>
        args.createdDate ? convertDate(Number(args.createdDate), "dateForTooltip") : "",
    },
    {
      id: "responseType",
      field: "responseType",
      name: t("Response Type"),
      minWidth: 150,
      formatter: responseTypeFormatter,
      filterType: "Text",
      _excelValueGetter: (args: MailParams) => responseTypeLabels[args.responseType || ""] || "-",
    },
    {
      id: "dueDate",
      field: "dueDate",
      name: t("Due Date"),
      minWidth: 150,
      formatter: dueDateFormatter,
      filterType: "Date",
      _excelValueGetter: (args: MailParams) =>
        args.dueDate ? convertDate(Number(args.dueDate), "dateForTooltip") : "",
    },
    {
      id: "status",
      field: "status",
      name: t("Status"),
      minWidth: 120,
      filterType: "Text",
    },
    {
      id: "tags",
      field: "tags",
      name: t("Tags"),
      minWidth: 160,
      formatter: tagsFormatter,
      filterType: "MultiSelect",
      _valueGetter: (args: MailParams) => (args.tags ?? []).join(", "),
      _excelValueGetter: (args: MailParams) => (args.tags ?? []).join(", "),
    },
  ];

  const headerThreeDotsItems: MailboxMenuItem[] = [{ text: "Copy Link", id: "copyLink" }];
  const handleSelectOption = (args: { item: { properties: { id?: string } } }) => {
    if (args.item.properties.id == "copyLink") {
      handleCopy();
    }
  };

  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(`${projectData.mailboxAddress}@${projectData.mailboxDomain}`);
      dispatch(
        showToastNotification({
          content: t("Mail address copied to clipboard."),
        }),
      );
    } catch (error: unknown) {
      dispatch(
        showToastNotification({
          content: t("Cannot Copy to Clipboard - {{message}}", { message: (error as Error).message }),
          type: TOAST_TYPE_ERROR,
        }),
      );
      Promise.reject(error);
    }
  };

  const toolbarOptions = [
    {
      id: "custom_createMail",
      name: t("Create"),
      prefixIcon: "add",
      disabled: false,
    },
    {
      id: "custom_deleteMail",
      name: t("Delete"),
      prefixIcon: "delete",
      disabled: disableToolbarButtons.deleteMail,
    },
  ];

  const handleDeleteMailModalClose = () => {
    setShowDeleteConfirmationModal(false);
  };

  const handleRowSelection = (args: Record<string, unknown>) => {
    // Untyped SlickGrid event payload: its shape is declared once here; the selected row ids are
    // the mail `_id`s the grid was loaded with.
    const detail = args?.detail as { args?: { selectedRowIds?: ObjectIdString[] } } | undefined;
    const ids = detail?.args?.selectedRowIds ?? [];
    setSelectedRowIds(ids);
    if (!ids.length) {
      setOpenMailSidebar(false);
      setDisableToolbarButtons({ deleteMail: true, openSidebar: true });
    } else if (ids.length === 1) {
      setDisableToolbarButtons({ deleteMail: false, openSidebar: false });
    } else {
      setDisableToolbarButtons({ deleteMail: true, openSidebar: true });
    }
  };

  const handleDeleteMails = async () => {
    const mailIds = selectedRowIds;
    const response = await deleteThreadRequest({ mailIds });
    if (response.data.status) {
      let message = t("Selected mail(s) successfully deleted.");
      if (response.data.mailsNoAccess.length) {
        message = t("Mail(s) {{mailNumbers}} cannot be deleted due to insufficient permissions.", {
          mailNumbers: response.data.mailsNoAccess.join(", "),
        });
      }
      setShowDeleteConfirmationModal(false);
      refreshDataGrid();
      if (response.data?.status) {
        dispatch(
          showToastNotification({
            content: message,
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(response.data.errorMessage || ""),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    }
  };

  const deletePopup = () => (
    <div>
      <ModalStateDialogComponent
        footerContent={
          <div className="flex gap-4">
            <ButtonComponent
              onClick={handleDeleteMailModalClose}
              buttonType="borderButton"
              data-tesid="cancelDeletingMail"
            >
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
            <ButtonComponent onClick={handleDeleteMails} buttonType="gradientButton" data-tesid="deleteMail">
              <Text>{t("Delete")}</Text>
            </ButtonComponent>
          </div>
        }
        onCancel={handleDeleteMailModalClose}
        headerTitle={t("Delete Mail")}
        isVisible={showDeleteConfirmationModal}
        modelContent={t("Are you sure you want to delete all selected mails?")}
      />
    </div>
  );

  const handleSetFormTemplate = async (formIds: ObjectIdString[]) => {
    if (formIds.length) {
      const response = await getTemplates({
        formId: formIds,
      });
      if (response.data.result) {
        let templates: FormTemplateSection[][] = [];
        const result = response.data.result;
        if (Array.isArray(result)) {
          for (const res of result) {
            templates.push((res as { template: FormTemplateSection[] }).template);
          }
        } else {
          templates.push((result as { template: FormTemplateSection[] }).template);
        }
        setFormTemplates(templates);
      }
    }
  };

  const handleDataBound = (dataView: unknown): MailRow[] => {
    const { result = [], count } = (dataView as { data?: { result?: MailRow[]; count?: number } })?.data ?? {};
    const hasData = (count || result.length) > 0;
    const hasFilters = (dataGrid.current?.appliedFilters?.length ?? 0) > 0;
    if (hasData || !hasFilters) {
      setHasGridData(hasData);
    }
    if (result.length) {
      const formIds = new Set<ObjectIdString>();
      for (const record of result) {
        if (record.formTemplate?._id) {
          formIds.add(record.formTemplate._id);
        }
      }
      if (formIds.size > 0) {
        handleSetFormTemplate([...formIds]);
      }
    }
    return result;
  };

  useEffect(() => {
    setColumnDefinitions(baseColumns);
  }, []);

  useEffect(() => {
    const templateColumns: MailGridColumn[] = [];
    if (formTemplates.length) {
      for (const template of formTemplates) {
        for (const section of template) {
          if (section.sectionCode !== presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION) {
            // getSubColumns' private `Section`/return types are stricter/looser than the
            // server form-template section; cast at this single external boundary.
            const sectionColumns = getSubColumns(
              section as Parameters<typeof getSubColumns>[0],
            ) as unknown as MailGridColumn[];
            templateColumns.push(...sectionColumns);
          }
        }
      }
    }

    setColumnDefinitions([...baseColumns, ...templateColumns]);
  }, [formTemplates]);

  const handleExcelExport = async () => {
    const gridState = dataGrid.current?.gridState;
    if (!gridState) return;

    dispatch(
      showToastNotification({
        content: t(
          "Preparing export(s). This may take a while if the number of items is large. You will receive an email once the export is complete.",
        ),
      }),
    );

    try {
      const sortConditions: SortCondition[] = gridState?.sortConditions ?? [];
      const filterPredicates = gridState?.filterPredicates;
      const groupConditions: GroupCondition[] = (gridState?.groupConditions ?? []).map((gc: GroupCondition) => ({
        column: gc.column,
        direction: gc.direction,
        preserveOrder: gc.preserveOrder,
      }));

      const columns: MailGridColumn[] =
        (dataGrid.current?.slickGrid?.getColumns() as unknown as MailGridColumn[] | undefined)?.filter(
          (col) => !col.excludeFromExport && col.id !== "_checkbox_selector",
        ) ?? [];

      const mailsResponse = await getMailsRequest({
        projectId: gridQuery.current.projectId,
        // `viewId` is read off the live grid-state object but is not part of the
        // public GridStateSnapshot type, so narrow just this property access.
        viewId: (gridState as { viewId?: ObjectIdString }).viewId,
        value: {
          skip: 0,
          take: 0,
          sorted: sortConditions.map((c) => ({
            name: c.column,
            direction: c.direction === "asc" ? ("ascending" as const) : ("descending" as const),
          })),
          where: filterPredicates && Object.keys(filterPredicates).length > 0 ? [filterPredicates] : undefined,
          requiresCounts: false,
        },
      });

      const mails: MailRow[] = mailsResponse?.data?.result ?? [];

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Mailbox");

      const headerRow = worksheet.addRow(columns.map((col) => col.name));
      headerRow.font = { bold: true };
      columns.forEach((_, i: number) => {
        worksheet.getColumn(i + 1).width = 20;
      });

      const getCellValue = (mail: MailRow, col: MailGridColumn): string => {
        let val: unknown;
        if (typeof col._excelValueGetter === "function") val = col._excelValueGetter(mail);
        else if (typeof col._valueGetter === "function") val = col._valueGetter(mail);
        else val = _.get(mail, col.field);
        if (val === null || val === undefined) return "";
        if (Array.isArray(val))
          return val
            .map((v: unknown) => {
              const item = v as { name?: string; displayText?: string };
              return item?.name || item?.displayText || String(v);
            })
            .filter(Boolean)
            .join(", ");
        return String(val);
      };

      const getRowValues = (mail: MailRow): string[] => columns.map((col) => getCellValue(mail, col));

      type GroupNode = {
        label: string;
        value: string;
        count: number;
        subGroups?: GroupNode[];
        rows?: MailRow[];
      };

      const buildGroupTree = (rows: MailRow[], conditions: GroupCondition[], depth: number): GroupNode[] => {
        const gc = conditions[depth];
        if (!gc) return [];
        const col = columns.find((c) => (c.id || c.field) === gc.column);
        if (!col) return [];

        const groupMap = new Map<string, MailRow[]>();
        for (const mail of rows) {
          const value = getCellValue(mail, col);
          if (!groupMap.has(value)) groupMap.set(value, []);
          groupMap.get(value)!.push(mail);
        }

        const entries = Array.from(groupMap.entries());
        if (!gc.preserveOrder) {
          if (gc.direction === "desc") {
            entries.sort(([a], [b]) => b.localeCompare(a, undefined, { numeric: true }));
          } else {
            entries.sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
          }
        }

        const isLastLevel = depth === conditions.length - 1;
        return entries.map(([value, groupRows]) => ({
          label: col.name,
          value,
          count: groupRows.length,
          ...(isLastLevel ? { rows: groupRows } : { subGroups: buildGroupTree(groupRows, conditions, depth + 1) }),
        }));
      };

      const writeGroupsToSheet = (groups: GroupNode[], level: number): void => {
        for (const group of groups) {
          const groupHeaderRow = worksheet.addRow([`${group.label}: ${group.value} (${group.count} rows)`]);
          groupHeaderRow.font = { bold: true };
          groupHeaderRow.outlineLevel = level;
          if (group.subGroups) {
            writeGroupsToSheet(group.subGroups, level + 1);
          } else if (group.rows) {
            for (const mail of group.rows) {
              const dataRow = worksheet.addRow(getRowValues(mail));
              dataRow.outlineLevel = level + 1;
            }
          }
        }
      };

      if (groupConditions.length > 0) {
        const groups = buildGroupTree(mails, groupConditions, 0);
        writeGroupsToSheet(groups, 0);
      } else {
        for (const mail of mails) {
          worksheet.addRow(getRowValues(mail));
        }
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const awsKey = await uploadStringToTemp(blob, "mailbox_export.xlsx");

      if (!awsKey) {
        dispatch(showToastNotification({ content: t("Export failed. Please try again."), type: TOAST_TYPE_ERROR }));
        return;
      }

      await exportMailsToExcelRequest({
        projectId: gridQuery.current.projectId,
        awsKey,
        fileName: "mailbox_export.xlsx",
      });
    } catch {
      dispatch(showToastNotification({ content: t("Export failed. Please try again."), type: TOAST_TYPE_ERROR }));
    }
  };

  const toolbarClickHandler = (params: string) => {
    if (params === "custom_createMail") {
      setOpenNewMailModal(true);
    } else if (params === "custom_deleteMail") {
      setShowDeleteConfirmationModal(true);
    } else if (params === "custom_copyLink") {
      try {
        navigator.clipboard.writeText(`${projectData.mailboxAddress}@${projectData.mailboxDomain}`);
      } catch (error: unknown) {
        dispatch(
          showToastNotification({
            content: t("Cannot Copy to Clipboard - {{message}}", { message: (error as Error).message }),
            type: TOAST_TYPE_ERROR,
          }),
        );
        Promise.reject(error);
      }
    } else if (params === "properties" || params === "custom_property") {
      setOpenMailSidebar(!openMailSidebar);
    }
  };

  function refreshDataGrid() {
    if (dataGrid.current) {
      dataGrid.current.refresh?.();
    } else {
      setHasGridData(true);
    }
  }

  const recordClick = (args: GridClickEvent) => {
    const rowIndex = args.detail.args.row;
    const clickedRow = dataGrid.current?.slickGrid?.getDataItem(rowIndex);
    if (!clickedRow?._id) return;
    const columns = dataGrid.current?.slickGrid?.getColumns() || [];
    const clickedColumn = columns[args.detail.args.cell];
    if (clickedColumn?.id !== "_checkbox_selector" && args.detail.args.cell > 0) {
      navigate(getProjectMailThreadsPageUrl(props.projectId, clickedRow._id));
    }
  };

  const noDataIllustration = () => (
    <NoMails setOpenNewMailModal={setOpenNewMailModal} setOpenForwardModal={setOpenForwardModal} />
  );

  const getTreeGrid = () => (
    <SlickGrid1
      ref={dataGrid}
      columnDefinitions={columnDefinitions as unknown as ValidatedColumnDefinition<MailRow>[]}
      remoteDataSource={mailsUrl}
      query={gridQuery.current}
      initialSortColumnId="createdDate"
      onClick={recordClick}
      toolbar={toolbarOptions}
      propertiesPanel={true}
      propertiesDisabled={disableToolbarButtons.openSidebar}
      toolbarClickHandler={toolbarClickHandler}
      enableFiltering={true}
      onSelectedRowIdsChanged={handleRowSelection}
      gridOptions={{
        enableSorting: true,
        showPreHeaderPanel: true,
        preHeaderPanelHeight: 30,
        topHeaderPanelHeight: 30,
      }}
      beforeDataBound={handleDataBound}
      enableFilteringOption
      enableSortingOption
      enableGroupingOption
      enableColumnChooser
      disableGridMargin
      excelExport
      excelExportCallback={handleExcelExport}
      infiniteScroll
      noDataIllustration={noDataIllustration}
      viewsOptions={{
        headerName: "Views",
        name: "View",
        resourceType: "MAILBOX",
        selectedLayoutKey: "MAILBOX_SELECTED_LAYOUT",
        fetchParams: {
          projectId: props.projectId,
        },
      }}
      enableViews={Boolean(props.projectId)}
    />
  );

  return (
    <>
      {showDeleteConfirmationModal && deletePopup()}
      {openForwardModal && (
        <ForwardModal
          handleCopy={handleCopy}
          onClose={() => setOpenForwardModal(false)}
          mailboxAddress={`${projectData.mailboxAddress}@${projectData.mailboxDomain}`}
        />
      )}
      {openNewMailModal ? (
        <CreateMail
          projectId={props.projectId}
          onClose={() => setOpenNewMailModal(false)}
          refreshData={refreshDataGrid}
        />
      ) : null}
      <GridPageLayout
        showHeader={true}
        gridContainerClassName="overflow-auto"
        customHeader={
          <MailboxHeader
            isExternalMailEnabled={isExternalMailEnabled}
            projectData={projectData}
            headerThreeDotsItems={headerThreeDotsItems}
            handleSelectOption={handleSelectOption}
          />
        }
      >
        {(hasGridData === null ? projectData.mailsCounter : hasGridData) ? (
          <div className="w-full h-full overflow-auto flex gap-2">
            <div className="h-full overflow-auto flex-1">{getTreeGrid()}</div>
            {openMailSidebar && selectedRowIds.length === 1 ? (
              <MailSidebar
                mailId={selectedRowIds[0]!}
                setOpenMailSidebar={setOpenMailSidebar}
                projectId={props.projectId}
                refreshData={refreshDataGrid}
              />
            ) : null}
          </div>
        ) : (
          noDataIllustration()
        )}
      </GridPageLayout>
    </>
  );
}

export default MailsList;
